/**
 * AccesszenProvider — fetches and caches PermissionSnapshot.
 *
 * Wraps the application with authorization context.
 * Fetches the user's permission snapshot from backend on mount
 * and on workspace changes.
 *
 * Stale-while-revalidate: if a recent snapshot exists in sessionStorage,
 * it is served immediately (loading=false) so that RequirePermission renders
 * gated content on first paint. A background re-fetch always runs to keep
 * the snapshot fresh. This eliminates the per-navigation loading flash that
 * would otherwise hide headings until the API responds.
 *
 * IMPORTANT: Frontend permission checks are UI hints only.
 * Backend always performs the authoritative check.
 */

import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { PermissionSnapshot } from '@platform/accesszen-contracts';
import { loadSnapshot, stateForOrg } from './snapshot-loader';
export type AccesszenContextValue = {
  snapshot: PermissionSnapshot | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /**
   * Background re-fetch that keeps the current snapshot (loading stays false),
   * so RequirePermission-gated UI does not unmount mid-interaction — e.g. after
   * the signed-in user changes their own agent role. Ref: ASC-10648
   */
  revalidate: () => Promise<void>;
};
export const AccesszenContext = createContext<AccesszenContextValue | null>(null);
type AccesszenProviderProps = {
  /**
   * Base URL for the permissions API endpoint.
   * Defaults to '/api/permissions' (relative, cookie auto-sent).
   */
  permissionsEndpoint?: string;
  /**
   * The signed-in organization. Required: the snapshot cache is keyed by it, so a
   * snapshot written in one org is never served in another and there is no unkeyed
   * path that skips that check. Ref: ASC-10848
   */
  orgId: string;
  children: React.ReactNode;
};
export function AccesszenProvider({
  permissionsEndpoint = '/api/permissions',
  orgId,
  children
}: AccesszenProviderProps): React.JSX.Element {
  // hasCachedRef is set during the useState lazy initializer so that fetchSnapshot
  // can decide whether its first call should be silent (cache warm) or foreground
  // (no cache — must show loading state). After the first fetch completes, all
  // subsequent calls (including explicit refresh()) run foreground.
  const hasCachedRef = useRef(false);
  const [snapshot, setSnapshot] = useState<PermissionSnapshot | null>(() => {
    const initial = stateForOrg(orgId);
    hasCachedRef.current = initial.snapshot !== null;
    return initial.snapshot;
  });
  const [loading, setLoading] = useState<boolean>(() => !hasCachedRef.current);
  const [error, setError] = useState<string | null>(null);

  // Bumped on every fetch and on every org change; a response whose sequence is no
  // longer current is dropped whole (state, cache, and its finally). Ref: ASC-10848
  const fetchSeqRef = useRef(0);
  const fetchSnapshot = useCallback(async () => {
    // Silent on first fetch when cache was warm — avoids a loading flash that
    // would hide RequirePermission content on each page.goto() in the same session.
    // Subsequent calls (explicit refresh, workspace change) always show loading.
    const silent = hasCachedRef.current;
    hasCachedRef.current = false;
    fetchSeqRef.current += 1;
    const seq = fetchSeqRef.current;
    await loadSnapshot({
      endpoint: permissionsEndpoint,
      orgId,
      silent,
      isCurrent: () => fetchSeqRef.current === seq,
      sink: {
        setSnapshot,
        setLoading,
        setError
      }
    });
  }, [permissionsEndpoint, orgId]);

  // hasCachedRef doubles as "make the next fetch silent" — the same path the
  // warm-cache first fetch takes. Ref: ASC-10648
  const revalidate = useCallback(() => {
    hasCachedRef.current = true;
    return fetchSnapshot();
  }, [fetchSnapshot]);

  // The org changed under a mounted provider: drop the previous org's snapshot now
  // rather than serving it until the fetch below lands. Declared BEFORE the fetch
  // effect so hasCachedRef is settled when fetchSnapshot reads it. Ref: ASC-10848
  const lastOrgRef = useRef(orgId);
  useEffect(() => {
    if (lastOrgRef.current === orgId) return;
    lastOrgRef.current = orgId;
    // Invalidate the previous org's in-flight fetch before the effect below starts B's.
    fetchSeqRef.current += 1;
    const next = stateForOrg(orgId);
    hasCachedRef.current = next.snapshot !== null;
    setSnapshot(next.snapshot);
    setLoading(next.loading);
  }, [orgId]);
  useEffect(() => {
    void fetchSnapshot();
  }, [fetchSnapshot]);
  const value: AccesszenContextValue = {
    snapshot,
    loading,
    error,
    refresh: fetchSnapshot,
    revalidate
  };
  return <AccesszenContext.Provider value={value}>{children}</AccesszenContext.Provider>;
}